<template>
    <div>
        <!-- Language Tabs -->
        <div v-if="loadingLanguages" class="text-center py-3">
            <font-awesome-icon :icon="['fas', 'spinner']" spin/>
            Cargando idiomas...
        </div>
        <ul v-else class="nav nav-pills mb-3">
            <li class="nav-item" v-for="lang in languages" :key="lang.id">
                <a class="nav-link"
                   :class="{ active: selectedLang && selectedLang.id === lang.id }"
                   href="#"
                   @click.prevent="selectLanguage(lang)">
                    {{ lang.name }}
                    <span class="badge badge-light ml-1">{{ lang.iso }}</span>
                </a>
            </li>
        </ul>

        <!-- Loading JSON -->
        <div v-if="loadingJson" class="text-center py-4">
            <font-awesome-icon :icon="['fas', 'spinner']" spin/>
            Cargando traducciones...
        </div>

        <!-- No file found -->
        <div v-else-if="selectedLang && !jsonData" class="alert alert-warning">
            No existe el archivo <strong>itinerary.json</strong> para el idioma
            <strong>{{ selectedLang.name }} ({{ selectedLang.iso }})</strong> en el servicio de Stela.
        </div>

        <!-- Editor -->
        <div v-else-if="selectedLang && jsonData">
            <!-- Toolbar -->
            <div class="row align-items-center mb-3">
                <div class="col-md-5">
                    <input v-model="searchQuery"
                           id="search-query"
                           aria-label="Buscar clave"
                           class="form-control form-control-sm"
                           placeholder="Buscar clave...">
                </div>
                <div class="col-md-7 text-right">
                    <small class="text-muted mr-2">{{ rows.length }} claves</small>
                    <button class="btn btn-success btn-sm" @click="saveJson" :disabled="saving">
                        <font-awesome-icon :icon="['fas', saving ? 'spinner' : 'save']" :spin="saving" class="mr-1"/>
                        {{ saving ? 'Guardando...' : 'Guardar' }}
                    </button>
                </div>
            </div>

            <!-- Table -->
            <div class="table-responsive" style="max-height: 65vh; overflow-y: auto;">
                <table class="table table-sm table-bordered table-hover mb-0">
                    <thead class="thead-light" style="position: sticky; top: 0; z-index: 1;">
                        <tr>
                            <th style="width: 32%;">Clave</th>
                            <th>Valor</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in rows"
                            :key="row._key"
                            :class="{ 'table-secondary': row.type === 'array-header' }">
                            <!-- Array header: spans full width -->
                            <td v-if="row.type === 'array-header'" colspan="2" class="py-1">
                                <code class="text-dark font-weight-bold">{{ row.key }}</code>
                            </td>
                            <!-- Key column (string or array-item) -->
                            <td v-if="row.type !== 'array-header'"
                                class="align-middle"
                                style="font-size: 0.8rem;">
                                <code class="text-secondary"
                                      :style="row.type === 'array-item' ? 'margin-left: 12px' : ''">
                                    {{ row.displayKey }}
                                </code>
                            </td>
                            <!-- Value column -->
                            <td v-if="row.type !== 'array-header'">
                                <textarea v-if="isLongText(row.value)"
                                          :id="'field-' + row._key"
                                          :aria-label="row.displayKey"
                                          class="form-control form-control-sm"
                                          :value="row.value"
                                          @input="onInput(row, $event.target.value)"
                                          rows="3"
                                          style="resize: vertical;"></textarea>
                                <input v-else
                                       :id="'field-' + row._key"
                                       :aria-label="row.displayKey"
                                       type="text"
                                       class="form-control form-control-sm"
                                       :value="row.value"
                                       @input="onInput(row, $event.target.value)">
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-else-if="!loadingLanguages" class="text-center text-muted py-4">
            Selecciona un idioma para comenzar.
        </div>
    </div>
</template>

<script>
  import { API } from '../../api'

  export default {
    data () {
      return {
        languages: [],
        selectedLang: null,
        jsonData: null,
        loadingLanguages: false,
        loadingJson: false,
        saving: false,
        searchQuery: ''
      }
    },
    computed: {
      rows () {
        if (!this.jsonData) return []

        const q = this.searchQuery.toLowerCase()
        const entries = Object.entries(this.jsonData)

        const result = []
        for (const [key, value] of entries) {
          if (Array.isArray(value)) {
            if (!q || key.toLowerCase().includes(q)) {
              result.push({ type: 'array-header', key, _key: 'hdr-' + key })
              value.forEach((item, index) => {
                result.push({
                  type: 'array-item',
                  key,
                  index,
                  displayKey: `[${index}] ${'name' in item ? 'name' : Object.keys(item)[0]}`,
                  value: 'name' in item ? item.name : Object.values(item)[0],
                  _key: `arr-${key}-${index}`
                })
              })
            }
          } else if (typeof value === 'string') {
            if (!q || key.toLowerCase().includes(q) || value.toLowerCase().includes(q)) {
              result.push({
                type: 'string',
                key,
                displayKey: key,
                value,
                _key: 'str-' + key
              })
            }
          }
        }
        return result
      }
    },
    mounted () {
      this.loadLanguages()
    },
    methods: {
      loadLanguages () {
        this.loadingLanguages = true
        API.get('languages').then(res => {
          if (res.data.success) {
            this.languages = Object.values(res.data.data)
            if (this.languages.length) {
              this.selectLanguage(this.languages[0])
            }
          }
        }).finally(() => {
          this.loadingLanguages = false
        })
      },
      selectLanguage (lang) {
        this.selectedLang = lang
        this.jsonData = null
        this.searchQuery = ''
        this.loadingJson = true
        API.get(`itinerary-translations/stela/${lang.iso}`)
          .then(res => {
            if (res.data && res.data.success !== undefined) {
              this.jsonData = res.data.success ? res.data.data : null
            } else if (res.data && typeof res.data === 'object') {
              this.jsonData = res.data
            }
          })
          .catch(() => {
            this.jsonData = null
          })
          .finally(() => {
            this.loadingJson = false
          })
      },
      isLongText (value) {
        return typeof value === 'string' && value.length > 80
      },
      onInput (row, value) {
        if (row.type === 'string') {
          this.$set(this.jsonData, row.key, value)
        } else if (row.type === 'array-item') {
          const arr = this.jsonData[row.key].map((item, i) => {
            if (i !== row.index) return item
            const fieldKey = 'name' in item ? 'name' : Object.keys(item)[0]
            return { ...item, [fieldKey]: value }
          })
          this.$set(this.jsonData, row.key, arr)
        }
      },
      saveJson () {
        this.saving = true
        API.put(`itinerary-translations/stela/${this.selectedLang.iso}`, { data: this.jsonData })
          .then(() => {
            this.$notify({
              group: 'main',
              type: 'success',
              title: 'Guardado',
              text: `itinerary.json (${this.selectedLang.iso}) guardado correctamente en Stela.`
            })
          })
          .catch(() => {
            this.$notify({
              group: 'main',
              type: 'error',
              title: 'Error',
              text: 'No se pudo guardar el archivo.'
            })
          })
          .finally(() => {
            this.saving = false
          })
      }
    }
  }
</script>
